/**
 * Infer extendable vehicle class from Core /policies/risk/:riskId rows.
 * Core often sends agnShtDesc as an agency code (e.g. SD00001) — do not use that for product class.
 */

function textFromProductFields(policy) {
  const parts = [
    policy?.product,
    policy?.productDesc,
    policy?.productDescription,
    policy?.className,
    policy?.vehicleClass,
    policy?.vehicle_class,
    policy?.riskClass,
    policy?.riskDescription,
    policy?.subClass,
    policy?.subClassDesc
  ]
    .filter(Boolean)
    .map((s) => String(s).trim());
  return parts.join(' ').toUpperCase();
}

/**
 * Map HDO/{productCode}/… policy numbers when Core omits product text (agency-only agnShtDesc).
 * Aligns with server policy blocks (see policy_number_sequence / seed_tor_class_mappings).
 */
export function inferVehicleClassFromPolicyNo(policyNo) {
  const raw = String(policyNo || '').trim().toUpperCase();
  const m = raw.match(/^HDO\/([0-9]{4})\//);
  if (!m) return null;
  const code = m[1];
  if (code === '0700' || code === '0703') return 'Motor Private';
  if (code === '0701') return 'Motorcycle Private';
  if (code === '0800') return 'Motor Commercial';
  if (code === '0807') return 'Motor Commercial General Cartage';
  if (code === '0813') return 'Motor Commercial Institutional';
  if (code === '0816') return 'Motor Commercial';
  if (code === '0804' || code === '0818') return 'PSV Chauffer Driven';
  if (code === '0810') return 'Tuk Tuk PSV';
  if (code === '0817') return null;
  return null;
}

/**
 * @returns {string|null} One of the extendable labels or null if not inferable / not extendable
 */
export function inferExtendableVehicleClass(policy) {
  const s = textFromProductFields(policy);
  if (s) {
    if (s.includes('MOTOR COMMERCIAL INSTITUTIONAL')) return 'Motor Commercial Institutional';
    if (s.includes('MOTOR COMMERCIAL GENERAL CARTAGE')) return 'Motor Commercial General Cartage';
    if (s.includes('MOTOR COMMERCIAL')) return 'Motor Commercial';
    // Private motorcycle only — PSV motorcycle / boda text must not map to Motorcycle Private.
    if (
      (s.includes('MOTOR CYCLE') || s.includes('MOTORCYCLE')) &&
      !s.includes('PSV') &&
      !s.includes('BODA')
    ) {
      return 'Motorcycle Private';
    }
    if (s.includes('MOTOR PRIVATE')) return 'Motor Private';
    if (s.includes('TUK') && s.includes('PSV')) return 'Tuk Tuk PSV';
    if (s.includes('TUKTUK') && s.includes('PSV')) return 'Tuk Tuk PSV';
    if (s.includes('PSV')) return 'PSV Chauffer Driven';
  }
  const fromNo = inferVehicleClassFromPolicyNo(policy?.policyNo || policy?.policy_number);
  return fromNo || null;
}

function formatPortalCoverLabel(coverType, vehicleClass) {
  const ct = String(coverType || '').trim();
  const vc = String(vehicleClass || '').trim();
  if (ct && vc) return `${ct} · ${vc}`;
  return vc || ct || '';
}

/**
 * Merge portal-issued identity (certificate / DB) over Core risk rows for extend finder display.
 * Portal wins for insured name, cover label, and vehicle class; Core keeps balances and batch metadata.
 */
export function mergeExtendFinderPolicyRow(portalRow, coreRow) {
  const policyNo = String(portalRow?.policyNo || coreRow?.policyNo || coreRow?.policy_number || '')
    .trim();
  const portalVehicleClass = String(
    portalRow?.firstSegmentVehicleClass || portalRow?.vehicleClass || ''
  ).trim();
  const coverType = String(portalRow?.coverType || '').trim();
  const coverLabel = formatPortalCoverLabel(coverType, portalVehicleClass);

  return {
    ...(coreRow || {}),
    policyNo,
    policy_number: policyNo,
    clientName: String(portalRow?.insuredName || coreRow?.clientName || coreRow?.client_name || '').trim(),
    portalInsuredName: String(portalRow?.insuredName || '').trim(),
    portalVehicleClass: portalVehicleClass || null,
    portalCoverType: coverType || null,
    portalCertificateNo: portalRow?.dmvicCertificateNumber || null,
    portalFirstSegmentStartDate: portalRow?.firstSegmentStartDate || null,
    displayProduct: coverLabel,
    productDesc: coverLabel || coreRow?.productDesc
  };
}

/**
 * Extend finder: policy is eligible when Core inference or portal first-segment class is allowed.
 */
export function isExtendableForExtension(policy, portalVehicleClass, allowedClasses) {
  const allowed =
    allowedClasses instanceof Set ? allowedClasses : new Set(allowedClasses || []);
  const coreClass = inferExtendableVehicleClass(policy);
  if (coreClass && allowed.has(coreClass)) return true;
  const portal = String(portalVehicleClass || '').trim();
  return !!(portal && allowed.has(portal));
}

export function resolveExtensionVehicleClass(portalVehicleClass, coreInferredClass, allowedClasses) {
  const allowed =
    allowedClasses instanceof Set ? allowedClasses : new Set(allowedClasses || []);
  const pick = (value) => {
    const v = String(value || '').trim();
    return v && allowed.has(v) ? v : null;
  };
  return (
    pick(portalVehicleClass) ||
    pick(coreInferredClass) ||
    (allowed.has('Motor Private') ? 'Motor Private' : [...allowed][0] || 'Motor Private')
  );
}
